// AI controllers for every function the player does not perform. They use the same radio, the same
// instructions and the same pilots as the player – realistic, not perfect: reaction times, standard
// separations with a margin, vectoring by headings with periodic corrections.
import type { Role } from '../data/types';
import type { World } from './world';
import type { Pilot } from './pilot';
import type { Aircraft } from './aircraft';
import type { Cmd } from './commands';
import type { RunwayEnd } from './airport';
import { distOnFinal } from './aircraft';
import { NM, dist, bearing, angDiff, norm360, alongTrack, crossTrack, DEG } from './geo';
import { wakeDepSec, wakeSepNm, type Wake } from './perf';

interface AcMemo { holdFl?: boolean; lastTx: number; released?: boolean; holding?: boolean; resolvedT?: number; slot?: number; holdUntil?: number; holdAlt?: number; plan?: { side: number; phase: 'DIRECT' | 'DOWNWIND' | 'BASE' | 'INTERCEPT' }; extendNm?: number; lastHdg?: number; ilsCleared?: boolean; descended?: number; handedOff?: boolean; climbIssued?: number; spdIssued?: number }

export class AiControllers {
  private memo = new Map<string, AcMemo>();
  private acc = 0;
  constructor(private readonly w: World) {}

  private m(cs: string): AcMemo {
    let x = this.memo.get(cs);
    if (!x) { x = { lastTx: -999 }; this.memo.set(cs, x); }
    return x;
  }
  private active(role: Role): boolean { return !this.w.isPlayerRole(role) && !!this.w.unitFor(role); }
  private send(role: Role, p: Pilot, cmds: Cmd[], station?: boolean): void {
    const unit = this.w.unitFor(role)!;
    if (p.freq !== unit) return;
    this.w.atcTransmit(unit, p.cs, cmds, false, station ? this.w.unitCallsign(unit) : undefined);
    this.m(p.cs).lastTx = this.w.t;
  }
  private ready(p: Pilot, cooldown = 6): boolean {
    return p.contacted && this.w.t - this.m(p.cs).lastTx > cooldown && !this.w.frequencyBusy(p.freq ?? '') && !p.pendingRb && !this.w.awaitingReaction(p.cs);
  }
  /** Is the pilot on the frequency of a unit that performs this role (and is the role handled by the AI)? */
  private on(p: Pilot, role: Role): boolean { return this.active(role) && p.freq === this.w.unitFor(role); }

  update(dt: number): void {
    this.acc += dt;
    if (this.acc < 1) return;
    this.acc = 0;
    this.resolveConflicts();
    for (const p of [...this.w.pilots.values()]) {
      if (!p.freq || p.stage === 'GONE') continue;
      this.delivery(p);
      this.ground(p);
      this.tower(p);
      this.departure(p);
      this.approach(p);
      this.center(p);
    }
  }

  private req(p: Pilot, kind: string): { t: number } | undefined {
    return this.w.requests.find(r => r.cs === p.cs && r.kind === kind);
  }

  // ------------------------------------------------------------------ delivery
  private delivery(p: Pilot): void {
    if (!this.on(p, 'DEL')) return;
    const r = this.req(p, 'CLEARANCE');
    if (r && this.w.t - r.t > 5 && this.ready(p)) {
      const c = this.w.defaultClearance(p);
      if (c) this.send('DEL', p, [c]);
      return;
    }
    if (p.stage === 'PRE' && p.clearance && this.ready(p, 2)) this.send('DEL', p, [{ k: 'RB_OK' }]);
  }

  // ------------------------------------------------------------------ ground
  private ground(p: Pilot): void {
    const w = this.w, ac = p.ac;
    const gndRole: Role = 'GND';
    if (!this.on(p, gndRole)) return;
    const pushReq = this.req(p, 'PUSH') ?? this.req(p, 'STARTUP');
    if (pushReq && w.t - pushReq.t > 4 && this.ready(p)) {
      if (this.pushbackClear(ac)) this.send(gndRole, p, [{ k: p.ac.stand?.push === false ? 'STARTUP' : 'PUSH' }]);
      return;
    }
    const taxiReq = this.req(p, 'TAXI');
    if (taxiReq && w.t - taxiReq.t > 3 && this.ready(p)) {
      const hold = w.chooseDepHold(p);
      if (hold) {
        const rwy = p.depRwy ?? w.config.dep[0]!;
        const via = w.aiTaxiRoute(p, { kind: 'HOLD', rwy, hold: hold.name });
        if (via) this.send(gndRole, p, [{ k: 'TAXI', target: { kind: 'HOLD', rwy, hold: hold.name }, via }]);
      }
      return;
    }
    const vac = this.req(p, 'VACATED');
    if (vac && w.t - vac.t > 3 && this.ready(p)) {
      let stand = ac.fp.stand && w.airport.stands[ac.fp.stand] ? ac.fp.stand : w.freeStandFor(ac);
      // stand not reachable for this aircraft (wing span limits) → another one
      if (stand && !w.suggestTaxiVia(p, { kind: 'STAND', stand })) { w.standOcc.delete(stand); stand = w.freeStandFor(ac); }
      if (stand) {
        ac.fp.stand = stand;
        const via = w.aiTaxiRoute(p, { kind: 'STAND', stand });
        if (via) this.send(gndRole, p, [{ k: 'TAXI', target: { kind: 'STAND', stand }, via }]);
      }
      return;
    }
    // departure approaching the holding point → tower
    if (p.isDeparture && (p.stage === 'TAXIING' || p.stage === 'AT_HOLD') && p.depHold && this.ready(p, 8)) {
      const hn = w.airport.nodes[p.depHold.node]!;
      const twr = w.unitFor('TWR');
      if (twr && twr !== p.freq && (dist(ac.x, ac.y, hn.x, hn.y) < 350 || p.stage === 'AT_HOLD')) this.send(gndRole, p, [{ k: 'CONTACT', unit: twr }]);
    }
    // runway crossing (ground coordinates with tower)
    const wait = ac.waitingAt();
    if (wait?.startsWith('RWY:') && this.ready(p, 5)) {
      const rwId = wait.slice(4);
      if (this.runwayFreeFor(rwId, 90)) this.send(gndRole, p, [{ k: 'CROSS', rwy: w.airport.runways.find(r => r.id === rwId)!.endIds[0] }]);
    }
  }

  private pushbackClear(ac: Aircraft): boolean {
    const w = this.w;
    const st = ac.stand;
    if (!st) return true;
    const ex = w.airport.nodes[st.apronExit]!;
    return !w.aircraft.some(o => o !== ac && o.onGround && (o.gmode === 'PUSHBACK' || o.gmode === 'TAXI' || o.gmode === 'VACATING') && dist(o.x, o.y, ex.x, ex.y) < 110);
  }

  /** Runway free of traffic and no arrival closer than the given time. */
  private runwayFreeFor(rwId: string, sec: number): boolean {
    const w = this.w;
    if (w.runwayOccupants(rwId).length) return false;
    for (const endId of w.config.arr) {
      const end = w.airport.runwayEnds[endId]!;
      if (end.runway.id !== rwId) continue;
      const next = w.finalSequence(end)[0];
      if (next && next.d < (next.ac.gs * sec) / 3600 + 1) return false;
    }
    return true;
  }

  // ------------------------------------------------------------------ tower
  private tower(p: Pilot): void {
    const w = this.w, ac = p.ac;
    if (!this.on(p, 'TWR')) return;
    if (p.isDeparture) {
      if (!ac.onGround) {
        if (ac.altFt - w.meta.elevFt > 700 && this.ready(p, 4)) {
          const dep = w.unitFor('DEP');
          if (dep && dep !== p.freq) this.send('TWR', p, [{ k: 'CONTACT', unit: dep }]);
        }
        return;
      }
      const rwy = p.depRwy ?? w.config.dep[0]!;
      const end = w.airport.end(rwy);
      if ((p.stage === 'AT_HOLD' || p.stage === 'TAXIING') && this.ready(p)) {
        if (p.stage === 'TAXIING' && p.depHold) {
          const hn = w.airport.nodes[p.depHold.node]!;
          if (dist(ac.x, ac.y, hn.x, hn.y) > 120) return;
        }
        if (this.canLineUp(p, end)) this.send('TWR', p, [{ k: 'LINEUP', rwy, hold: p.depHold && p.depHold !== end.depHolds[0] ? p.depHold.name : undefined }]);
        return;
      }
      if (p.stage === 'LINEUP' && ac.gmode === 'LINED_UP' && !p.clearedTakeoff && this.ready(p, 4)) {
        if (this.canTakeoff(p, end)) this.send('TWR', p, [{ k: 'TAKEOFF', rwy }]);
      }
      return;
    }
    // arrivals
    if (!ac.onGround && p.stage === 'FINAL' && p.arrRwy) {
      const end = w.airport.end(p.arrRwy);
      const d = distOnFinal(ac, end);
      if (!p.clearedLand && d < 7 && this.ready(p, 4)) {
        if (this.landingClear(p, end, d)) this.send('TWR', p, [{ k: 'LAND', rwy: p.arrRwy }]);
        else if (d < 1.4) this.send('TWR', p, [{ k: 'GO_AROUND' }]);
        else if (this.req(p, 'CHECKIN_FINAL')) this.send('TWR', p, [{ k: 'CONTINUE' }], true);
      } else if (!p.clearedLand && d >= 7 && this.req(p, 'CHECKIN_FINAL') && this.ready(p, 3)) {
        this.send('TWR', p, [{ k: 'CONTINUE' }], true);
      } else if (p.clearedLand && d < 1.3 && d > 0.2 && !this.runwayClearForLanding(p, end) && !this.w.awaitingReaction(p.cs)) {
        // runway not vacated in time – go around
        this.send('TWR', p, [{ k: 'GO_AROUND' }]);
      }
      return;
    }
    if (!ac.onGround && p.stage === 'ARRIVING' && ac.goAround && ac.altFt - w.meta.elevFt > 1200 && this.ready(p, 4)) {
      const app = w.unitFor('APP');
      if (app && app !== p.freq) this.send('TWR', p, [{ k: 'CONTACT', unit: app }]);
      return;
    }
    if (ac.onGround && (p.stage === 'LANDED') && (ac.gmode === 'VACATED' || ac.gmode === 'VACATING') && this.ready(p, 4)) {
      const gnd = w.unitFor('GND');
      if (gnd && gnd !== p.freq) this.send('TWR', p, [{ k: 'CONTACT', unit: gnd }]);
    }
  }

  private nextArrival(end: RunwayEnd): { ac: Aircraft; d: number } | null {
    const w = this.w;
    let best: { ac: Aircraft; d: number } | null = null;
    for (const endId of w.config.arr) {
      const e = w.airport.runwayEnds[endId]!;
      if (e.runway !== end.runway) continue;
      for (const s of w.finalSequence(e)) {
        const pp = w.pilot(s.ac.cs);
        if (!pp || pp.stage === 'LANDED' || s.ac.onGround || s.ac.goAround) continue;
        const d = distOnFinal(s.ac, e);
        if (d < -0.5 || d > 25) continue;
        if (!best || d < best.d) best = { ac: s.ac, d };
      }
    }
    return best;
  }

  private departureInterval(p: Pilot, end: RunwayEnd): number {
    const last = this.w.lastDeparture;
    if (!last) return 0;
    const sameExit = last.exit && p.clearance?.sid && last.exit.split(' ')[0] === p.clearance.sid.split(' ')[0];
    const need = Math.max(sameExit ? 120 : 75, wakeDepSec(last.wake, p.ac.type.wake, p.depHold !== end.depHolds[0]));
    return need - (this.w.t - last.t);
  }

  private canLineUp(p: Pilot, end: RunwayEnd): boolean {
    const w = this.w;
    const occ = w.runwayOccupants(end.runway.id).filter(a => a !== p.ac);
    if (occ.some(a => !(a.gmode === 'TAKEOFF' && a.ias > 40) && !(!a.onGround))) return false;
    // only one aircraft lining up / waiting on the runway at a time
    for (const o of w.pilots.values()) {
      if (o === p || o.depRwy === null || w.airport.runwayEnds[o.depRwy]?.runway !== end.runway) continue;
      if (o.stage === 'LINEUP' || (o.stage === 'TAKEOFF' && o.ac.onGround && o.ac.ias < 40)) return false;
    }
    const arr = this.nextArrival(end);
    const lineupTime = 45, takeoffTime = 40;
    const gap = this.departureInterval(p, end);
    if (arr && arr.d < ((arr.ac.gs || 140) * (lineupTime + Math.max(0, gap) + takeoffTime)) / 3600 + 2.5) return false;
    return gap < 40;
  }

  private canTakeoff(p: Pilot, end: RunwayEnd): boolean {
    const w = this.w;
    const mine = w.airport.along(end, p.ac);
    // traffic behind us on the runway (e.g. lining up at the threshold) is not in the way
    const occ = w.runwayOccupants(end.runway.id).filter(a => a !== p.ac && (!a.onGround || w.airport.along(end, a) > mine - 40));
    if (occ.length) return false;
    const arr = this.nextArrival(end);
    if (arr && arr.d < 3) return false;
    return this.departureInterval(p, end) <= 0;
  }

  /** At short final: the runway must be free (departures airborne, landings vacated). */
  private runwayClearForLanding(p: Pilot, end: RunwayEnd): boolean {
    return !this.w.runwayOccupants(end.runway.id).some(a => a !== p.ac && !(a.gmode === 'TAKEOFF' && !a.onGround) && !(a.gmode === 'TAKEOFF' && a.ias > 100));
  }

  private landingClear(p: Pilot, end: RunwayEnd, d: number): boolean {
    const occ = this.w.runwayOccupants(end.runway.id).filter(a => a !== p.ac);
    for (const a of occ) {
      if (a.gmode === 'TAKEOFF' && (a.ias > 60 || !a.onGround)) continue;
      if (a.gmode === 'ROLLOUT' && d > 2.5) continue;
      if (a.gmode === 'VACATING' && d > 1.6) continue;
      return false;
    }
    // a departure lined up must be rolling before we clear the landing
    return true;
  }

  // ------------------------------------------------------------------ departure radar
  private departure(p: Pilot): void {
    const w = this.w, ac = p.ac;
    if (!this.on(p, 'DEP') || ac.onGround || !p.isDeparture) return;
    const r = this.req(p, 'CHECKIN_DEP');
    const memo = this.m(p.cs);
    if (r && this.ready(p, 2)) {
      const fl = Math.min(p.ac.fp.rfl, 100);
      const ft = fl * 100;
      const cmds: Cmd[] = [{ k: 'IDENTIFIED' }];
      if (this.levelChangeSafe(ac, this.flToAlt(fl))) { cmds.push({ k: 'ALT', ft, fl: true }); memo.climbIssued = ft; }
      this.send('DEP', p, cmds, true);
      return;
    }
    if (!memo.climbIssued && this.ready(p, 10)) {
      const fl = Math.min(p.ac.fp.rfl, 100);
      if (this.levelChangeSafe(ac, this.flToAlt(fl))) { this.send('DEP', p, [{ k: 'ALT', ft: fl * 100, fl: true }]); memo.climbIssued = fl * 100; }
    }
    if (ac.amode === 'HDG' && memo.resolvedT !== undefined && w.t - memo.resolvedT > 45 && ac.fp.exitFix && this.ready(p, 10) && !w.safety.stca.some(c => c.a === ac.cs || c.b === ac.cs)) {
      this.send('DEP', p, [{ k: 'DIRECT', fix: ac.fp.exitFix }]);
      memo.resolvedT = undefined;
      return;
    }
    const ctr = w.unitFor('CTR');
    const sector = w.unit(w.unitFor('DEP')!).sector;
    if (ctr && ctr !== p.freq && !memo.handedOff && (dist(ac.x, ac.y, 0, 0) > ((sector?.radiusNm ?? 30) - 8) * NM || ac.pressureAlt(w.weather.qnh) > 9000) && this.ready(p, 6)) {
      memo.handedOff = true;
      this.send('DEP', p, [{ k: 'CONTACT', unit: ctr }]);
    }
  }

  private flToAlt(fl: number): number { return fl * 100 - (1013.25 - this.w.weather.qnh) * 27.3; }

  /** Conflict probe: no aircraft within 8 NM whose level lies in the band we would climb/descend through. */
  private levelChangeSafe(ac: Aircraft, tgtAlt: number): boolean {
    const lo = Math.min(ac.altFt, tgtAlt) - 950, hi = Math.max(ac.altFt, tgtAlt) + 950;
    for (const o of this.w.aircraft) {
      if (o === ac || o.onGround) continue;
      const d = dist(ac.x, ac.y, o.x, o.y) / NM;
      if (d > 9) continue;
      const oLo = Math.min(o.altFt, o.tgtAltFt), oHi = Math.max(o.altFt, o.tgtAltFt);
      if (oHi >= lo && oLo <= hi) {
        // diverging traffic further than 5 NM is fine
        const brg = bearing(ac.x, ac.y, o.x, o.y);
        const closing = Math.abs(angDiff(ac.trk, brg)) < 90 || Math.abs(angDiff(o.trk, norm360(brg + 180))) < 90;
        if (closing || d < 5) return false;
      }
    }
    return true;
  }

  // ------------------------------------------------------------------ approach radar
  private approach(p: Pilot): void {
    const w = this.w, ac = p.ac;
    if (!this.on(p, 'APP') || ac.onGround || p.isDeparture) return;
    const memo = this.m(p.cs);
    const r = this.req(p, 'CHECKIN_ARR');
    const end = w.airport.end(p.arrRwy ?? w.config.arr[0]!);
    if (memo.slot === undefined && p.contacted) this.assignSlot(ac, end, memo);
    if (r && this.ready(p, 2)) {
      const cmds: Cmd[] = [{ k: 'IDENTIFIED' }];
      const alt = memo.holdAlt ?? 5000;
      const altTrue = memo.holdFl ? this.flToAlt(alt / 100) : alt;
      if (this.levelChangeSafe(ac, altTrue)) { cmds.push({ k: 'ALT', ft: alt, fl: !!memo.holdFl }); memo.descended = altTrue; }
      this.send('APP', p, cmds, true);
      return;
    }
    if (!this.ready(p, 8)) return;
    const d = dist(ac.x, ac.y, end.thr.x, end.thr.y) / NM;
    // hand-off to the tower once established
    if (ac.locCap && distOnFinal(ac, end) < 11) {
      const twr = w.unitFor('TWR');
      if (twr && twr !== p.freq) { this.send('APP', p, [{ k: 'CONTACT', unit: twr }]); return; }
    }
    if (ac.locCap) return;
    // ---------- sequencing (arrival manager): hold at the IAF until the slot allows the approach
    if (!memo.released) {
      if (w.t >= (memo.holdUntil ?? 0)) memo.released = true;
      else {
        const iaf = w.meta.stars.find(s => s.entry === ac.fp.entryFix)?.iaf;
        const pos = iaf ? w.fixPos(iaf) : null;
        if (iaf && pos && !memo.holding && dist(ac.x, ac.y, pos.x, pos.y) < 8 * NM) {
          memo.holding = true;
          this.send('APP', p, [{ k: 'HOLD_AT', fix: iaf }, { k: 'ALT', ft: memo.holdAlt ?? 5000, fl: !!memo.holdFl }]);
        }
        return;
      }
    }
    // ---------- descent profile
    const wantAlt = d < 16 ? 3000 : d < 25 ? 4000 : 5000;
    if ((memo.descended ?? 1e9) > wantAlt && this.levelChangeSafe(ac, wantAlt)) {
      memo.descended = wantAlt;
      this.send('APP', p, [{ k: 'ALT', ft: wantAlt, fl: false }]);
      return;
    }
    // ---------- radar pattern: committed side and phase, only forward (downwind → base → intercept)
    const crs = end.meta.ils?.course ?? end.hdg;
    const along = alongTrack(ac, end.thr, crs) / NM; // negative before the threshold
    const xt = crossTrack(ac, end.thr, crs) / NM; // + right of the inbound course
    if (!memo.plan) {
      const straightIn = along < -11 && Math.abs(xt) < Math.abs(along) * 0.6;
      const side = Math.abs(xt) > 0.5 ? Math.sign(xt) : Math.sign(angDiff(crs, ac.trk)) || 1;
      memo.plan = { side, phase: straightIn ? 'DIRECT' : along > -12 ? 'DOWNWIND' : 'BASE' };
    }
    const plan = memo.plan;
    // keep the slot: extend the downwind when early
    if (plan.phase === 'DOWNWIND' && memo.slot !== undefined) {
      const eta = w.t + (this.trackToGo(ac, end) / Math.max(150, ac.gs * 0.85)) * 3600;
      const early = memo.slot - eta;
      if (early > 20) memo.extendNm = Math.min(20, (memo.extendNm ?? 0) + Math.min(2, (early * ac.gs) / 3600 / 2));
      else if (early < -30) memo.extendNm = Math.max(0, (memo.extendNm ?? 0) - 1);
    }
    const ext = memo.extendNm ?? 0;
    const baseAt = 12 + ext;
    if (plan.phase === 'DOWNWIND' && along < -baseAt) plan.phase = 'BASE';
    if ((plan.phase === 'BASE' || plan.phase === 'DIRECT') && Math.abs(xt) < 2.4 && along < -7) plan.phase = 'INTERCEPT';
    let hdgTrue: number;
    switch (plan.phase) {
      case 'DOWNWIND': {
        // towards the downwind line (offset 5 NM), then parallel to the final in the opposite direction.
        // Flying the reciprocal course, a turn to the left increases the cross-track distance.
        const off = plan.side * 5 - xt;
        hdgTrue = norm360(crs + 180 - Math.max(-60, Math.min(60, off * 18)));
        break;
      }
      case 'BASE': hdgTrue = norm360(crs - plan.side * 90); break;
      case 'DIRECT': hdgTrue = norm360(crs - Math.sign(xt || 1) * Math.min(30, Math.abs(xt) * 10)); break;
      default: hdgTrue = norm360(crs - Math.sign(xt || plan.side) * 30);
    }
    const hdgMag = Math.round(w.trueToMag(this.windCorrected(ac, hdgTrue)) / 5) * 5 || 360;
    const cmds: Cmd[] = [];
    if (plan.phase === 'INTERCEPT') {
      if (!memo.ilsCleared) {
        cmds.push({ k: 'HDG', hdg: hdgMag }, { k: 'ILS', rwy: end.id });
        memo.ilsCleared = true;
        memo.lastHdg = hdgMag;
      }
    } else if (memo.lastHdg === undefined || Math.abs(angDiff(memo.lastHdg, hdgMag)) >= 10) {
      cmds.push({ k: 'HDG', hdg: hdgMag });
      memo.lastHdg = hdgMag;
    }
    // speed: 220 on downwind, 180 on base and intercept; slower when catching up with the preceding arrival
    let want = plan.phase === 'DOWNWIND' || plan.phase === 'DIRECT' ? (d < 25 ? 220 : 250) : 180;
    const lead = this.preceding(ac, end);
    if (lead && lead.gap < lead.need + 1) want = 160;
    const wantSpd = Math.max(want, ac.type.minClean - 15);
    if (memo.spdIssued !== wantSpd && wantSpd < 250) { cmds.push({ k: 'SPD', kt: wantSpd }); memo.spdIssued = wantSpd; }
    if (cmds.length) this.send('APP', p, cmds);
  }

  /** Arrival manager: landing slot = earliest time, but at least the required spacing after the previous slot. */
  private assignSlot(ac: Aircraft, end: RunwayEnd, memo: AcMemo): void {
    const w = this.w;
    const est = this.trackToGo(ac, end) * 1.3 + 6;
    const eta = w.t + (est / 210) * 3600;
    let prev: { slot: number; wake: Wake } | null = null;
    for (const o of w.aircraft) {
      const om = this.memo.get(o.cs);
      if (o === ac || om?.slot === undefined || w.pilot(o.cs)?.arrRwy !== end.id) continue;
      if (!prev || om.slot > prev.slot) prev = { slot: om.slot, wake: o.type.wake };
    }
    const sepNm = prev ? Math.max(6, wakeSepNm(prev.wake, ac.type.wake) + 2) : 0;
    const slot = prev ? Math.max(eta, prev.slot + (sepNm / 140) * 3600) : eta;
    memo.slot = slot;
    const delay = slot - eta;
    if (delay > 360) {
      memo.holdUntil = w.t + delay - 240;
      // holding stack: 1000 ft above the aircraft already holding at the same fix
      const iaf = w.meta.stars.find(s => s.entry === ac.fp.entryFix)?.iaf;
      let stack = 0;
      for (const [cs, m] of this.memo) {
        const o = w.ac(cs);
        if (cs === ac.cs || !o || m.holdAlt === undefined || m.released) continue;
        if (w.meta.stars.find(s => s.entry === o.fp.entryFix)?.iaf === iaf) stack++;
      }
      // lowest holding level at the transition altitude, above it flight levels from the transition level up
      memo.holdAlt = stack === 0 ? w.meta.transitionAltFt : (w.tl + (stack - 1) * 10) * 100;
      memo.holdFl = stack > 0;
    } else {
      memo.released = true;
      memo.extendNm = Math.max(0, (delay * 210) / 3600 / 2);
    }
  }

  /** The arrival ahead in the sequence and the current spacing (NM along the track). */
  private preceding(ac: Aircraft, end: RunwayEnd): { a: Aircraft; gap: number; need: number } | null {
    const w = this.w;
    const mine = this.trackToGo(ac, end);
    let best: { a: Aircraft; d: number } | null = null;
    for (const o of w.aircraft) {
      if (o === ac || o.onGround || w.pilot(o.cs)?.arrRwy !== end.id) continue;
      const d = this.trackToGo(o, end);
      if (d < mine && (!best || d > best.d)) best = { a: o, d };
    }
    if (!best) return null;
    return { a: best.a, gap: mine - best.d, need: Math.max(5, wakeSepNm(best.a.type.wake, ac.type.wake)) };
  }

  private windCorrected(ac: Aircraft, crsTrue: number): number {
    const wind = this.w.env().wind(ac.altFt);
    const cross = wind.kt * Math.sin(angDiff(crsTrue, wind.dir) * DEG);
    return norm360(crsTrue + Math.asin(Math.max(-0.5, Math.min(0.5, cross / Math.max(120, ac.tas)))) / DEG);
  }

  /** Remaining track distance to the threshold along the planned pattern (NM). */
  private trackToGo(a: Aircraft, end: RunwayEnd): number {
    const crs = end.meta.ils?.course ?? end.hdg;
    if (a.locCap) return distOnFinal(a, end);
    const al = alongTrack(a, end.thr, crs) / NM, xt = Math.abs(crossTrack(a, end.thr, crs) / NM);
    const m = this.memo.get(a.cs);
    const base = 12 + (m?.extendNm ?? 0);
    switch (m?.plan?.phase) {
      case 'DOWNWIND': return Math.max(0, al + base) + Math.abs(xt) + base;
      case 'BASE': case 'INTERCEPT': return xt * 1.15 + Math.max(-al, 6);
      default: return Math.hypot(al, xt);
    }
  }

  /** Radar controller reaction to a (predicted) conflict: stop the climb/descent 1000 ft clear, else turn away. */
  private resolveConflicts(): void {
    const w = this.w;
    for (const c of w.safety.stca) {
      const A = w.ac(c.a), B = w.ac(c.b);
      if (!A || !B) continue;
      const pick = this.pickResolver(A, B) ?? this.pickResolver(B, A);
      if (!pick) continue;
      const { me, other, role } = pick;
      const p = w.pilot(me.cs)!;
      const memo = this.m(me.cs);
      if (w.t - (memo.resolvedT ?? -999) < 25 || !this.ready(p, 2)) continue;
      const ta = w.meta.transitionAltFt;
      const lvl = (ft: number): Cmd => (ft > ta ? { k: 'ALT', ft: Math.round((ft + (1013.25 - w.weather.qnh) * 27.3) / 1000) * 1000, fl: true } : { k: 'ALT', ft: Math.round(ft / 1000) * 1000, fl: false });
      const changing = Math.abs(me.tgtAltFt - me.altFt) > 300;
      let cmd: Cmd | null = null;
      if (changing) {
        const below = me.altFt < other.altFt;
        const stopAt = below ? Math.floor((other.altFt - 1000) / 1000) * 1000 : Math.ceil((other.altFt + 1000) / 1000) * 1000;
        const ok = below ? stopAt >= me.altFt - 400 : stopAt <= me.altFt + 400;
        const mva = w.meta.mva.find(m => dist(me.x, me.y, 0, 0) / NM <= m.radiusNm)?.ft ?? 3000;
        if (ok && stopAt >= mva) cmd = lvl(stopAt);
      }
      if (!cmd && !me.locCap) {
        const away = norm360(bearing(other.x, other.y, me.x, me.y));
        const turn = angDiff(me.hdg, away) > 0 ? 1 : -1;
        cmd = { k: 'HDG', hdg: Math.round(w.trueToMag(norm360(me.hdg + turn * 40)) / 5) * 5 || 360, turn: turn > 0 ? 'R' : 'L' };
        if (memo.plan) { memo.plan = undefined; memo.lastHdg = undefined; memo.ilsCleared = false; }
      }
      if (cmd) { this.send(role, p, [cmd]); memo.resolvedT = w.t; }
    }
  }

  private pickResolver(me: Aircraft, other: Aircraft): { me: Aircraft; other: Aircraft; role: Role } | null {
    const p = this.w.pilot(me.cs);
    if (!p || me.onGround || me.locCap || me.altFt - this.w.meta.elevFt < 1200) return null;
    const role = (['DEP', 'APP', 'CTR'] as Role[]).find(r => this.on(p, r));
    if (!role) return null;
    // prefer the aircraft that is changing level
    const meChanging = Math.abs(me.tgtAltFt - me.altFt) > 300, otherChanging = Math.abs(other.tgtAltFt - other.altFt) > 300;
    if (!meChanging && otherChanging && this.pickable(other)) return null;
    return { me, other, role };
  }
  private pickable(a: Aircraft): boolean {
    const p = this.w.pilot(a.cs);
    return !!p && !a.onGround && !a.locCap && (['DEP', 'APP', 'CTR'] as Role[]).some(r => this.on(p, r));
  }

  onGoAround(p: Pilot): void {
    const memo = this.m(p.cs);
    memo.ilsCleared = false; memo.lastHdg = undefined; memo.descended = 3000; memo.spdIssued = undefined; memo.extendNm = 0; memo.plan = undefined; memo.released = true; memo.holding = false; memo.slot = undefined;
  }

  // ------------------------------------------------------------------ center
  private center(p: Pilot): void {
    const w = this.w, ac = p.ac;
    if (!this.on(p, 'CTR') || ac.onGround) return;
    const memo = this.m(p.cs);
    if (p.isDeparture) {
      const r = this.req(p, 'CHECKIN_DEP');
      const fl = ac.fp.rfl;
      if ((r || !memo.climbIssued) && this.ready(p, r ? 2 : 10)) {
        if (this.levelChangeSafe(ac, this.flToAlt(fl))) { this.send('CTR', p, [{ k: 'IDENTIFIED' }, { k: 'ALT', ft: fl * 100, fl: true }], !!r); memo.climbIssued = fl * 100; }
        else if (r) this.send('CTR', p, [{ k: 'IDENTIFIED' }], true);
      }
      return;
    }
    // arrival: descend to FL80 and hand over to approach about 30 NM out
    const chk = this.req(p, 'CHECKIN_ARR');
    if (chk && this.ready(p, 2)) {
      const cmds: Cmd[] = [{ k: 'IDENTIFIED' }];
      if (memo.descended === undefined && this.levelChangeSafe(ac, this.flToAlt(80))) { cmds.push({ k: 'ALT', ft: 8000, fl: true }); memo.descended = 8000; }
      this.send('CTR', p, cmds, true);
      return;
    }
    if (memo.descended === undefined && this.ready(p, 6)) {
      if (this.levelChangeSafe(ac, this.flToAlt(80))) { this.send('CTR', p, [{ k: 'ALT', ft: 8000, fl: true }]); memo.descended = 8000; }
      return;
    }
    const app = w.unitFor('APP');
    if (app && app !== p.freq && !memo.handedOff && dist(ac.x, ac.y, 0, 0) < 32 * NM && this.ready(p, 6)) {
      memo.handedOff = true;
      this.send('CTR', p, [{ k: 'CONTACT', unit: app }]);
    }
  }
}
